import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, Clock, Car, Navigation, ExternalLink, RefreshCw, AlertCircle } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { getPublicApiBaseUrl } from '@/utils/publicApi';
import { ROUTE_LINE_Z, fitMapToMarkers, syncActiveServicesOnMap } from '@/utils/liveVehicleMapLayers';
import { updateDrivingRoutesOnMap } from '@/utils/drivingRoute';

const REFRESH_MS = 30_000;
const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
const DEFAULT_ZOOM = 6;
const TRAIL_COLOR = '#16a34a';

function getCoords(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const lat = obj.lat ?? obj.latitude;
  const lng = obj.lng ?? obj.longitude;
  if (lat == null || lng == null) return null;
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln, updated_at: obj.updated_at, speed: obj.speed, heading: obj.heading };
}

function statusBadge(status, t) {
  const norm = String(status || '').toLowerCase();
  if (['delivered', 'completed'].includes(norm)) {
    return { label: t('public_tracking_status_delivered', { defaultValue: 'Entregado' }), bg: 'bg-blue-100', text: 'text-blue-700' };
  }
  if (['in_transit', 'in_progress', 'en_route', 'picked_up'].includes(norm)) {
    return { label: t('public_tracking_status_in_route', { defaultValue: 'En camino' }), bg: 'bg-emerald-100', text: 'text-emerald-700' };
  }
  if (['assigned', 'reserved'].includes(norm)) {
    return { label: t('public_tracking_status_assigned', { defaultValue: 'Asignado' }), bg: 'bg-amber-100', text: 'text-amber-700' };
  }
  if (['cancelled', 'canceled'].includes(norm)) {
    return { label: t('public_tracking_status_cancelled', { defaultValue: 'Cancelado' }), bg: 'bg-red-100', text: 'text-red-700' };
  }
  return { label: t('public_tracking_status_scheduled', { defaultValue: 'Programado' }), bg: 'bg-gray-100', text: 'text-gray-700' };
}

const PublicTracking = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsFailed, setMapsFailed] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [routeEta, setRouteEta] = useState(null);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const overlaysRef = useRef([]);
  const routeOverlaysRef = useRef([]);
  const trailRef = useRef(null);
  const routeFetchSeqRef = useRef(0);
  const hasFittedMapRef = useRef(false);

  const baseUrl = useMemo(() => getPublicApiBaseUrl(), []);

  const fetchData = useCallback(
    async (silent = false) => {
      if (!token) return;
      if (!silent) setLoading(true);
      setRefreshing(true);
      try {
        const url = `${baseUrl}/api/tracking/${encodeURIComponent(token)}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          if (res.status === 404) {
            setError('not_found');
            setData(null);
          } else {
            setError('server_error');
          }
          return;
        }
        const json = await res.json();
        setData(json);
        setError(null);
        setLastUpdate(new Date());
      } catch (e) {
        setError('network_error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [baseUrl, token]
  );

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Same Google Maps loader as admin + client GPS tabs
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setMapsReady(true);
      })
      .catch((err) => {
        console.warn('[PublicTracking] Google Maps failed to load:', err?.message || err);
        if (!cancelled) setMapsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const initializeMap = useCallback((container) => {
    if (!container || mapInstanceRef.current || !window.google?.maps) return;
    try {
      mapInstanceRef.current = new window.google.maps.Map(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });
      setMapInitialized(true);
    } catch (err) {
      console.error('[PublicTracking] Map init failed:', err);
      setMapsFailed(true);
    }
  }, []);

  const mapRef = useCallback(
    (node) => {
      mapContainerRef.current = node;
      if (node && mapsReady && !mapInstanceRef.current) {
        initializeMap(node);
      }
    },
    [mapsReady, initializeMap]
  );

  useEffect(() => {
    if (mapsReady && mapContainerRef.current && !mapInstanceRef.current) {
      initializeMap(mapContainerRef.current);
    }
  }, [mapsReady, initializeMap]);

  const currentGps = data ? getCoords(data.current_location) : null;
  const pickupGps = data ? getCoords(data.pickup_location) : null;
  const deliveryGps = data ? getCoords(data.delivery_location) : null;

  // Build one "service" in the same shape as admin/client GPS tabs
  const trackingServices = useMemo(() => {
    if (!data) return [];
    return [
      {
        id: data.id || data.order_id || token || 'tracking',
        vehicle_info: data.vehicle_info || {},
        pickup_location: data.pickup_location,
        delivery_location: data.delivery_location,
        current_location: data.current_location,
        eta_minutes: data.eta_minutes,
      },
    ];
  }, [data, token]);

  // Same marker + road-route stack as ClientGPSLiveTab / GPSLiveTab
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized || !data) return;

    const bounds = new window.google.maps.LatLngBounds();
    const { hasValidLocations, livePositions, skipped } = syncActiveServicesOnMap({
      map: mapInstanceRef.current,
      activeServices: trackingServices,
      markersRef,
      overlaysRef,
      bounds,
      getCurrentGps: (s) => getCoords(s.current_location),
      getPickupGps: (s) => getCoords(s.pickup_location),
      getDeliveryGps: (s) => getCoords(s.delivery_location),
      getPlate: (s) => s.vehicle_info?.plate,
      t,
    });

    if (hasValidLocations && !skipped && !hasFittedMapRef.current) {
      fitMapToMarkers(mapInstanceRef.current, bounds, { livePositions });
      hasFittedMapRef.current = true;
    }

    let cancelled = false;
    const seq = ++routeFetchSeqRef.current;
    updateDrivingRoutesOnMap({
      map: mapInstanceRef.current,
      activeServices: trackingServices,
      routeOverlaysRef,
      getCurrentGps: (s) => getCoords(s.current_location),
      getPickupGps: (s) => getCoords(s.pickup_location),
      getDeliveryGps: (s) => getCoords(s.delivery_location),
    }).then((etas) => {
      if (cancelled || seq !== routeFetchSeqRef.current) return;
      const first = Object.values(etas)[0] || null;
      setRouteEta(first);
    });

    return () => {
      cancelled = true;
    };
  }, [trackingServices, mapInitialized, data, t]);

  // Road already driven: GPS points from location_history (oldest first) up to the car.
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized || !data) return;
    const path = (Array.isArray(data.location_history) ? data.location_history : [])
      .map((p) => getCoords(p))
      .filter(Boolean)
      .map(({ lat, lng }) => ({ lat, lng }));
    const car = getCoords(data.current_location);
    if (car) path.push({ lat: car.lat, lng: car.lng });

    if (trailRef.current) {
      trailRef.current.setMap(null);
      trailRef.current = null;
    }
    if (path.length < 2) return;
    trailRef.current = new window.google.maps.Polyline({
      map: mapInstanceRef.current,
      path,
      strokeColor: TRAIL_COLOR,
      strokeOpacity: 0.9,
      strokeWeight: 5,
      zIndex: ROUTE_LINE_Z + 1,
    });
  }, [data, mapInitialized]);

  useEffect(() => {
    return () => {
      if (trailRef.current) {
        trailRef.current.setMap(null);
        trailRef.current = null;
      }
      markersRef.current.forEach((m) => m.setMap(null));
      overlaysRef.current.forEach((o) => o.setMap(null));
      routeOverlaysRef.current.forEach((o) => {
        if (typeof o.setMap === 'function') o.setMap(null);
      });
      markersRef.current = [];
      overlaysRef.current = [];
      routeOverlaysRef.current = [];
      mapInstanceRef.current = null;
    };
  }, []);

  const formatTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  };

  const formatScheduled = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(dateLocale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('public_tracking_loading', { defaultValue: 'Cargando seguimiento...' })}</span>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('public_tracking_not_found_title', { defaultValue: 'Seguimiento no disponible' })}
          </h1>
          <p className="text-gray-600 text-sm">
            {t('public_tracking_not_found_body', {
              defaultValue: 'El enlace ha caducado o no es válido. Pide al remitente un enlace actualizado.',
            })}
          </p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('public_tracking_error_title', { defaultValue: 'No se pudo conectar' })}
          </h1>
          <p className="text-gray-600 text-sm mb-4">
            {t('public_tracking_error_body', { defaultValue: 'Comprueba tu conexión y vuelve a intentarlo.' })}
          </p>
          <button
            onClick={() => fetchData(false)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0a3042] text-white text-sm font-semibold hover:opacity-90"
          >
            <RefreshCw className="w-4 h-4" />
            {t('public_tracking_retry', { defaultValue: 'Reintentar' })}
          </button>
        </div>
      </div>
    );
  }

  const badge = statusBadge(data?.status, t);
  const plate = data?.vehicle_info?.plate || '—';
  const brand = data?.vehicle_info?.brand || '';
  const model = data?.vehicle_info?.model || '';
  const eta = routeEta?.durationValue != null
    ? Math.max(1, Math.round(routeEta.durationValue / 60))
    : data?.eta_minutes;

  const externalMapsUrl = currentGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${currentGps.lat},${currentGps.lng}`
    : pickupGps
      ? `https://www.google.com/maps/search/?api=1&query=${pickupGps.lat},${pickupGps.lng}`
      : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-[#0a3042] text-white shadow">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-white p-1.5 flex items-center justify-center">
              <span className="font-extrabold text-[#0a3042] text-sm">AG</span>
            </div>
            <div>
              <p className="font-bold leading-tight">AutoGo Delivery</p>
              <p className="text-xs text-white/70 leading-tight">
                {t('public_tracking_subtitle', { defaultValue: 'Seguimiento en vivo' })}
              </p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-[#0a3042]/5 flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-[#0a3042]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xl font-bold text-gray-900 tracking-tight">{plate}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {brand} {model}
                </p>
                {data?.driver_name && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('public_tracking_driver', { defaultValue: 'Conductor' })}:{' '}
                    <span className="font-medium text-gray-700">{data.driver_name}</span>
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => fetchData(false)}
              disabled={refreshing}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
              aria-label={t('public_tracking_refresh', { defaultValue: 'Actualizar' })}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {Number.isFinite(Number(eta)) && currentGps ? (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <Clock className="w-4 h-4 text-emerald-700 flex-shrink-0" />
              <span className="text-sm text-emerald-900">
                <span className="font-semibold">{t('public_tracking_eta_label', { defaultValue: 'ETA' })}:</span>{' '}
                <span className="font-bold">
                  {routeEta?.durationText || `${Math.round(Number(eta))} min`}
                </span>
                {routeEta?.distanceText ? (
                  <span className="text-emerald-700 text-xs ml-2">· {routeEta.distanceText}</span>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>

        {/* Google Maps — same engine as admin/client GPS */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="relative">
            {!mapsFailed ? (
              <>
                <div
                  ref={mapRef}
                  className="w-full h-[360px] sm:h-[440px] bg-gray-100"
                  data-testid="public-tracking-google-map"
                />
                {!mapInitialized && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">
                      {t('client_gps_loading_map', { defaultValue: 'Cargando mapa...' })}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-[360px] sm:h-[440px] bg-gray-100 flex flex-col items-center justify-center text-center px-6">
                <MapPin className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 max-w-xs">
                  {t('public_tracking_map_unavailable', {
                    defaultValue:
                      'No se pudo cargar Google Maps. Usa el enlace “Abrir en Google Maps” si está disponible.',
                  })}
                </p>
              </div>
            )}

            {externalMapsUrl && (
              <a
                href={externalMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg shadow text-xs font-semibold text-[#0a3042] hover:bg-gray-50"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('public_tracking_open_in_maps', { defaultValue: 'Abrir en Google Maps' })}
              </a>
            )}
          </div>

          {!mapsFailed && mapInitialized && (
            <div className="px-4 py-2 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                {t('client_gps_route_origin', { defaultValue: 'Origen' })}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                {t('client_gps_route_destination', { defaultValue: 'Destino' })}
              </span>
              <span className="flex items-center gap-1.5">
                <Car className="w-3 h-3 text-blue-600" />
                {t('gps_map_live_vehicle', { defaultValue: 'Vehículo en vivo' })}
              </span>
              <span className="flex items-center gap-1.5 text-blue-700">
                <span className="w-5 border-t-2 border-blue-600 inline-block" />
                {t('gps_map_route_to_destination', { defaultValue: 'Ruta al destino' })}
              </span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-3">
          <div className="flex items-start gap-3">
            <span className="mt-1 w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {t('public_tracking_pickup', { defaultValue: 'Recogida' })}
              </p>
              <p className="text-sm text-gray-900 break-words">
                {data?.pickup_location?.address || '—'}
              </p>
            </div>
          </div>
          <div className="border-t border-gray-100" />
          <div className="flex items-start gap-3">
            <span className="mt-1 w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {t('public_tracking_delivery', { defaultValue: 'Entrega' })}
              </p>
              <p className="text-sm text-gray-900 break-words">
                {data?.delivery_location?.address || '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-500">
              {t('public_tracking_scheduled', { defaultValue: 'Fecha programada' })}
            </p>
            <p className="font-medium text-gray-900">
              {formatScheduled(data?.scheduled_datetime)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">
              {t('public_tracking_last_position', { defaultValue: 'Última posición' })}
            </p>
            <p className="font-medium text-gray-900">
              {currentGps?.updated_at ? formatTime(currentGps.updated_at) : '—'}
            </p>
          </div>
          {lastUpdate && (
            <div>
              <p className="text-xs text-gray-500">
                {t('public_tracking_refresh_label', { defaultValue: 'Actualizado' })}
              </p>
              <p className="font-medium text-gray-900">
                {lastUpdate.toLocaleTimeString(dateLocale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 py-4 flex items-center justify-center gap-1.5">
          <Navigation className="w-3.5 h-3.5" />
          {t('public_tracking_auto_refresh_hint', {
            defaultValue: 'Esta página se actualiza automáticamente cada 30 segundos.',
          })}
        </p>
      </main>
    </div>
  );
};

export default PublicTracking;
